import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

interface WelcomeImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
}

const WelcomeImageModal: React.FC<WelcomeImageModalProps> = ({ isOpen, onClose, imageUrl }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 dark:bg-opacity-70 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.4, type: 'spring', stiffness: 300 }}
        className="relative max-w-3xl w-full max-h-[85vh] bg-white dark:bg-stone-900 rounded-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className={clsx(
            'absolute top-4 right-4 z-10 p-2 rounded-full',
            'bg-white dark:bg-stone-800 shadow-lg',
            'text-stone-800 dark:text-zinc-400 hover:text-stone-600 dark:hover:text-zinc-200',
            'transition-all duration-200 hover:bg-slate-100 dark:hover:bg-stone-700'
          )}
          aria-label="Close welcome popup"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Image Container */}
        <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-stone-800 dark:to-stone-900">
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 border-4 border-stone-300 dark:border-stone-600 border-t-orange-500 rounded-full animate-spin" />
            </div>
          )}
          <motion.img
            initial={{ opacity: 0 }}
            animate={{ opacity: imageLoaded ? 1 : 0 }}
            transition={{ duration: 0.5 }}
            src={imageUrl}
            alt="Welcome to our restaurant"
            onLoad={() => setImageLoaded(true)}
            className="w-full h-full object-contain p-4"
          />
        </div>

        {/* Footer with Close Button */}
        <div className="flex justify-center p-4 bg-slate-50 dark:bg-stone-800 border-t border-stone-200 dark:border-stone-700">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            className={clsx(
              'px-8 py-2.5 bg-orange-500 hover:bg-orange-600',
              'dark:bg-orange-600 dark:hover:bg-orange-700',
              'text-white font-semibold rounded-lg',
              'transition-colors duration-200'
            )}
          >
            Close
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default WelcomeImageModal;
